// RCF entity graph — centralized JSON-LD generators that consolidate
// Organization, Person (founder), WebSite, and sameAs signals across every
// RoofCertificateFlorida.com page. The whole graph is keyed by stable @ids
// so Google can resolve a single entity across the site.

import { COMPANY, PUBLIC_EMAIL } from "@/lib/constants";

const SITE = "https://roofcertificateflorida.com";

// Stable @ids — never change these once published.
export const RCF_IDS = {
  org: `${SITE}/#organization`,
  parentOrg: "https://obriencontractingusa.com/#organization",
  website: `${SITE}/#website`,
  founder: `${SITE}/#founder`,
  logo: `${SITE}/#logo`,
} as const;

// Verified external profiles for the parent business — these are the
// sameAs signals that consolidate the entity graph for Google.
export const SAME_AS = [
  "https://obriencontractingusa.com",
  "https://obriencontractingusa.com",
  COMPANY.social.facebook,
  COMPANY.social.instagram,
  COMPANY.social.yelp,
  // Florida DBPR licensee record (CCC1335947)
  "https://www.myfloridalicense.com/LicenseDetail.asp?SID=&id=CCC1335947",
] as const;

export const rcfOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": RCF_IDS.org,
  name: "RoofCertificateFlorida",
  alternateName: "Florida Roof Certificate by O'Brien Contracting",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    "@id": RCF_IDS.logo,
    url: `${SITE}/images/logo-transparent.png`,
    width: 512,
    height: 512,
  },
  parentOrganization: {
    "@type": "RoofingContractor",
    "@id": RCF_IDS.parentOrg,
    name: COMPANY.name,
    url: "https://obriencontractingusa.com",
    telephone: COMPANY.phoneFormatted,
    ...(PUBLIC_EMAIL ? { email: PUBLIC_EMAIL } : {}),
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.address.street,
      addressLocality: COMPANY.address.city,
      addressRegion: COMPANY.address.state,
      postalCode: COMPANY.address.zip,
      addressCountry: "US",
    },
    areaServed: { "@type": "State", name: "Florida" },
    foundingDate: String(COMPANY.founded),
    founder: { "@id": RCF_IDS.founder },
    identifier: {
      "@type": "PropertyValue",
      propertyID: "Florida Contractor License",
      value: COMPANY.license,
    },
  },
  founder: { "@id": RCF_IDS.founder },
  contactPoint: [{
    "@type": "ContactPoint",
    telephone: COMPANY.phoneFormatted,
    contactType: "customer service",
    areaServed: "US-FL",
    availableLanguage: ["English", "Spanish"],
  }],
  sameAs: SAME_AS,
});

export const rcfFounderSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": RCF_IDS.founder,
  name: "Christopher O'Brien",
  jobTitle: "Founder & Licensed Roofing Contractor",
  worksFor: { "@id": RCF_IDS.parentOrg },
  knowsAbout: [
    "Florida roof certification",
    "OIR-B1-1802 wind mitigation form",
    "4-point insurance inspection",
    "Florida Statute 627.711",
    "Hip roof affidavit",
    "Roof age verification",
  ],
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    credentialCategory: "license",
    name: "Florida Certified Roofing Contractor",
    identifier: COMPANY.license,
    recognizedBy: {
      "@type": "GovernmentOrganization",
      name: "Florida Department of Business and Professional Regulation",
      url: "https://www.myfloridalicense.com",
    },
  },
  sameAs: SAME_AS,
});

export const rcfWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": RCF_IDS.website,
  url: SITE,
  name: "RoofCertificateFlorida",
  publisher: { "@id": RCF_IDS.org },
  inLanguage: "en-US",
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE}/?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
});

// Combined @graph payload — drop this inside ONE <script type="application/ld+json">
// on every RCF page so the entity graph is consistent everywhere.
export const rcfEntityGraph = () => ({
  "@context": "https://schema.org",
  "@graph": [
    rcfOrganizationSchema(),
    rcfFounderSchema(),
    rcfWebsiteSchema(),
  ],
});
